import { PointerEvent, useEffect, useRef, useState } from 'react';

import { Box } from '@mui/material';

import {
  LINE_WIDTH_DIVIDER,
  MIN_LINE_WIDTH,
  STROKE_COLOR
} from '@/features/report-problem/lib/annotationStyle';
import {
  INormalizedRect,
  IPoint,
  pointsToRect
} from '@/features/report-problem/lib/rects';

/** Рамки меньше этого размера считаем случайным тапом и не сохраняем */
const MIN_RECT_SIZE_PX = 4;

interface IProps {
  screenshot: HTMLCanvasElement;
  rects: INormalizedRect[];
  onRectsChange: (rects: INormalizedRect[]) => void;
}

/**
 * Слой рисования рамок-выделений поверх скриншота.
 * Координаты рамок нормализованы, поэтому превью можно масштабировать
 * по ширине модалки, а в файл рамки лягут в полном разрешении.
 * @param root0 - пропсы
 * @param root0.screenshot - canvas со снимком экрана
 * @param root0.rects - уже нарисованные рамки
 * @param root0.onRectsChange - колбэк изменения списка рамок
 */
export const ScreenshotAnnotator = ({
  screenshot,
  rects,
  onRectsChange
}: IProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const startPointRef = useRef<IPoint | null>(null);
  const [draftRect, setDraftRect] = useState<INormalizedRect | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');

    if (!canvas || !context) return;

    canvas.width = screenshot.width;
    canvas.height = screenshot.height;

    context.drawImage(screenshot, 0, 0);
    context.strokeStyle = STROKE_COLOR;
    context.lineWidth = Math.max(
      MIN_LINE_WIDTH,
      canvas.width / LINE_WIDTH_DIVIDER
    );

    [...rects, ...(draftRect ? [draftRect] : [])].forEach(rect => {
      context.strokeRect(
        rect.x * canvas.width,
        rect.y * canvas.height,
        rect.width * canvas.width,
        rect.height * canvas.height
      );
    });
  }, [screenshot, rects, draftRect]);

  /**
   * Пересчитать координаты указателя в пиксели скриншота
   * @param event - событие указателя
   */
  const getPoint = (event: PointerEvent<HTMLCanvasElement>): IPoint => {
    const bounds = event.currentTarget.getBoundingClientRect();

    return {
      x: ((event.clientX - bounds.left) / bounds.width) * screenshot.width,
      y: ((event.clientY - bounds.top) / bounds.height) * screenshot.height
    };
  };

  /**
   * Начало рисования рамки
   * @param event - событие указателя
   */
  const handlePointerDown = (event: PointerEvent<HTMLCanvasElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    startPointRef.current = getPoint(event);
  };

  /**
   * Отрисовка «резиновой» рамки во время перетаскивания
   * @param event - событие указателя
   */
  const handlePointerMove = (event: PointerEvent<HTMLCanvasElement>) => {
    if (!startPointRef.current) return;

    setDraftRect(
      pointsToRect(
        startPointRef.current,
        getPoint(event),
        screenshot.width,
        screenshot.height
      )
    );
  };

  /**
   * Завершение рисования: слишком маленькие рамки игнорируем
   * @param event - событие указателя
   */
  const handlePointerUp = (event: PointerEvent<HTMLCanvasElement>) => {
    if (!startPointRef.current) return;

    const rect = pointsToRect(
      startPointRef.current,
      getPoint(event),
      screenshot.width,
      screenshot.height
    );

    startPointRef.current = null;
    setDraftRect(null);

    const isTooSmall =
      rect.width * screenshot.width < MIN_RECT_SIZE_PX ||
      rect.height * screenshot.height < MIN_RECT_SIZE_PX;

    if (isTooSmall) return;

    onRectsChange([...rects, rect]);
  };

  return (
    <Box
      component='canvas'
      ref={canvasRef}
      data-testid='screenshot-annotator'
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      // Размеры задаются только ограничениями: canvas — replaced-элемент с
      // intrinsic-размерами битмапа, поэтому он сам впишется в оба лимита с
      // сохранением пропорций. Растягивать по ширине нельзя: у портретного
      // превью бокс стал бы шире битмапа, contain дорисовал бы поля по бокам,
      // а getPoint считает координаты от бокса — рамка уезжала бы от курсора.
      sx={{
        cursor: 'crosshair',
        display: 'block',
        height: 'auto',
        margin: '0 auto',
        maxHeight: '40vh',
        maxWidth: '100%',
        touchAction: 'none',
        width: 'auto'
      }}
    />
  );
};
